/**
 * Abstract Authentication Client Interface
 *
 * Google Identity Platform (GCIP) auth client on firebase/auth. The webapp
 * hosts email/password sign-in + enforced TOTP MFA per agency GCIP tenant.
 * See lib/auth-client-gcip.ts and CJIS_EXCEPTIONS.md PE-21.
 */

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
}

export interface AuthClient {
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void;
  signIn(email: string, password: string): Promise<AuthUser>;
  signOut(): Promise<void>;
  /**
   * CJIS AC-11/SC-10 idle session lock. Blocklists the current bearer at
   * the backend, clears the session cookie, and signs out of Firebase so the
   * subsequent resume requires a fresh interactive sign-in + MFA. Unlike
   * signOut(), does NOT navigate — caller is expected to display a lock
   * overlay and route to /login on resume.
   */
  lockSession(): Promise<void>;
  /**
   * Best-effort re-establishment of the authenticated user when the
   * server-side session cookie is present but the in-tab client state was
   * not yet hydrated. Firebase persists auth in IndexedDB (shared across
   * tabs), so this typically just returns the restored current user once
   * its token is second-factor-attested and within the absolute cap.
   * Returns `null` when recovery is impossible — callers must fall through
   * to the standard unauthenticated path on `null`.
   */
  recoverSession(): Promise<AuthUser | null>;
  /**
   * Sign out of the Firebase client WITHOUT a full logout. Used by
   * `resetAuthSession()` to recover from a broken client state (e.g.
   * profile-fetch 401) without blocklisting the bearer or navigating to
   * `/logout`. After this call the client behaves as if no user was loaded;
   * the caller is responsible for any further redirect. Distinct from
   * `signOut()`, the user-initiated full-logout path.
   */
  clearLocalState(): Promise<void>;
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
  getCurrentUser(): AuthUser | null;
  reauthenticate(password: string): Promise<boolean>;
  updatePassword(currentPassword: string, newPassword: string): Promise<boolean>;
  /**
   * Request an in-boundary password reset link. When the user belongs to more
   * than one agency the forgot-password page resolves the tenants and passes
   * the selected `gcipTenantId` so the backend mints/sends a link for only that
   * pool; omitting it fans out to every pool the email is provisioned in.
   */
  sendPasswordResetEmail(email: string, gcipTenantId?: string | null): Promise<boolean>;
  /**
   * Send an in-boundary email-verification link to the currently authenticated
   * user. The backend mints the oobCode and emails a single-use link to the
   * app's own /auth/action handler via SendGrid (the default firebaseapp.com
   * handler is out-of-boundary). Returns false if not authenticated or on error.
   */
  sendEmailVerification(): Promise<boolean>;
  readonly providerName: string;
  readonly supportsPasswordManagement: boolean;
}

let _clientInstance: AuthClient | null = null;

export function getAuthClient(): AuthClient {
  if (_clientInstance) {
    return _clientInstance;
  }

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { GCIPAuthClient } = require('./auth-client-gcip');
  _clientInstance = new GCIPAuthClient();

  return _clientInstance!;
}
